import { useAppStore } from './stores/app';
import { buildAuthHeaders } from './authHeaders';
import type { paths as OpenApiPaths } from '../shared/openapi-types.generated';
import {
  createApiClient,
  fetchWithRetry,
  parseErrorMessage,
  parseJsonEnvelope,
  type ApiRequestOptions,
  type FetchRetryOptions,
  type RefreshAuthToken,
} from './apiClient';

type OpenApiPathTemplate = Extract<keyof OpenApiPaths, string>;
type MaterializeOpenApiPath<T extends string> =
  T extends `${infer A}{${string}}${infer B}`
    ? `${A}${string}${MaterializeOpenApiPath<B>}`
    : T;

export type ApiPath = MaterializeOpenApiPath<OpenApiPathTemplate>;
export type ApiPathWithQuery = ApiPath | `${ApiPath}?${string}`;

export interface PaginationMeta {
  total: number;
  page?: number;
  perPage?: number;
  totalPages?: number;
  limit?: number;
  offset?: number;
}

export interface PaginatedResponse<T = unknown> {
  data: T;
  meta: PaginationMeta | null;
}

export interface ApiDataWithStatus<T = unknown> {
  data: T;
  status: number;
}

const DEFAULT_WORKER_API_BASE = 'https://property-management.ryan-referme.workers.dev';

/** Empty for localhost (Vite proxies /api to localhost:8787), worker URL for remote hosts. */
export function getApiBase(
  env: { VITE_API_BASE_URL?: string } = import.meta.env as unknown as { VITE_API_BASE_URL?: string },
): string {
  const override = env.VITE_API_BASE_URL?.trim();
  if (override) {
    // Normalize a trailing slash to avoid double-slashes when callers use `/api/...`.
    return override.endsWith('/') ? override.slice(0, -1) : override;
  }

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }
  if (
    hostname.includes('.pages.dev') ||
    hostname.includes('property-management-frontend')
  ) {
    return DEFAULT_WORKER_API_BASE;
  }
  console.warn(
    `[API] Unknown frontend host "${hostname}" detected; defaulting API base to ${DEFAULT_WORKER_API_BASE}. ` +
    'Set VITE_API_BASE_URL for custom domains/environments.'
  );
  return DEFAULT_WORKER_API_BASE;
}

const API_BASE = getApiBase();

/**
 * Try to refresh the Clerk token on a 401 response.
 * Returns the new token if refresh succeeded, or null if it failed.
 * When null is returned the caller should treat the user as unauthenticated.
 */
const apiState = {
  token: null as string | null,
};
const inFlightPaginatedReads = new Map<string, Promise<PaginatedResponse<unknown>>>();

const refreshAuthToken: RefreshAuthToken = async () => {
  const appStore = useAppStore();
  if (appStore.isDemoMode || !window.Clerk?.session) return null;

  const newToken = await window.Clerk.session.getToken();
  if (newToken) {
    apiState.token = newToken;
    return newToken;
  }

  appStore.isAuthenticated = false;
  return null;
};

const client = createApiClient({
  baseUrl: API_BASE,
  getAuthHeaders: () => buildAuthHeaders(apiState.token),
  refreshAuthToken,
});

export const api = {
  baseUrl: API_BASE,

  setToken(token: string | null): void {
    apiState.token = token;
  },

  /**
   * Build Authorization headers for the current session.
   * Demo mode uses demo tokens; production uses Clerk JWTs.
   */
  getAuthHeaders(): Record<string, string> {
    return buildAuthHeaders(apiState.token);
  },

  /** Alias for getAuthHeaders() for fetch/export usage */
  getHeaders(): Record<string, string> {
    return this.getAuthHeaders();
  },

  async request<T = unknown>(
    method: string,
    path: ApiPathWithQuery,
    data: unknown = null,
    options?: ApiRequestOptions,
  ): Promise<T> {
    return client.request<T>(method, path, data, options);
  },

  get<T = unknown>(path: ApiPathWithQuery, options?: ApiRequestOptions): Promise<T> {
    return client.get<T>(path, options);
  },

  /** GET with dev-mode shape validation. Pass a validator for critical paths. */
  async validatedGet<T = unknown>(
    path: ApiPathWithQuery,
    validator?: (data: unknown, path: string) => boolean,
    options?: ApiRequestOptions,
  ): Promise<T> {
    const result = await this.get<T>(path, options);
    if (validator) {
      validator(result, path);
    }
    return result;
  },

  /** GET with pagination and dev-mode shape validation. */
  async validatedGetPaginated<T = unknown>(
    path: ApiPathWithQuery,
    validator?: (data: unknown, path: string) => boolean,
    options?: ApiRequestOptions,
  ): Promise<PaginatedResponse<T>> {
    const result = await this.getPaginated<T>(path, options);
    if (validator) {
      validator(result.data, path);
    }
    return result;
  },

  post<T = unknown>(path: ApiPathWithQuery, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return client.post<T>(path, data, options);
  },

  async postWithStatus<T = unknown>(
    path: ApiPathWithQuery,
    data?: unknown,
    options?: ApiRequestOptions,
  ): Promise<ApiDataWithStatus<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
    };
    for (const [key, value] of Object.entries(options?.headers ?? {})) {
      if (key.toLowerCase() === 'authorization') continue;
      headers[key] = value;
    }

    const response = await fetchWithRetry(
      `${this.baseUrl}${path}`,
      {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : null,
        signal: options?.signal,
      },
      {},
      refreshAuthToken,
    );

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }

    if (response.status === 204) {
      return { data: undefined as T, status: response.status };
    }

    const json = await parseJsonEnvelope(response);
    if (!('data' in json)) {
      throw new Error('Invalid API response: missing data envelope');
    }

    return {
      data: json.data as T,
      status: response.status,
    };
  },

  /** POST with dev-mode shape validation on the response. */
  async validatedPost<T = unknown>(
    path: ApiPathWithQuery,
    data?: unknown,
    validator?: (data: unknown, path: string) => boolean,
    options?: ApiRequestOptions,
  ): Promise<T> {
    const result = await this.post<T>(path, data, options);
    if (validator) {
      validator(result, path);
    }
    return result;
  },

  /** POST with shape validation and HTTP status metadata. */
  async validatedPostWithStatus<T = unknown>(
    path: ApiPathWithQuery,
    data?: unknown,
    validator?: (data: unknown, path: string) => boolean,
    options?: ApiRequestOptions,
  ): Promise<ApiDataWithStatus<T>> {
    const result = await this.postWithStatus<T>(path, data, options);
    if (validator) {
      validator(result.data, path);
    }
    return result;
  },

  put<T = unknown>(path: ApiPathWithQuery, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return client.put<T>(path, data, options);
  },

  /** PUT with dev-mode shape validation on the response. */
  async validatedPut<T = unknown>(
    path: ApiPathWithQuery,
    data?: unknown,
    validator?: (data: unknown, path: string) => boolean,
    options?: ApiRequestOptions,
  ): Promise<T> {
    const result = await this.put<T>(path, data, options);
    if (validator) {
      validator(result, path);
    }
    return result;
  },

  delete<T = unknown>(path: ApiPathWithQuery, options?: ApiRequestOptions): Promise<T> {
    return client.delete<T>(path, options);
  },

  /** DELETE with dev-mode shape validation on the response. */
  async validatedDelete<T = unknown>(
    path: ApiPathWithQuery,
    validator?: (data: unknown, path: string) => boolean,
    options?: ApiRequestOptions,
  ): Promise<T> {
    const result = await this.delete<T>(path, options);
    if (validator) {
      validator(result, path);
    }
    return result;
  },

  /**
   * Upload file(s) via FormData. Returns JSON data.
   */
  async upload<T = unknown>(
    path: ApiPathWithQuery,
    formData: FormData,
    options?: ApiRequestOptions,
  ): Promise<T> {
    return client.upload<T>(path, formData, options);
  },

  /** Upload with dev-mode shape validation on the response. */
  async validatedUpload<T = unknown>(
    path: ApiPathWithQuery,
    formData: FormData,
    validator?: (data: unknown, path: string) => boolean,
    options?: ApiRequestOptions,
  ): Promise<T> {
    const result = await this.upload<T>(path, formData, options);
    if (validator) {
      validator(result, path);
    }
    return result;
  },

  /**
   * GET request returning raw HTML (for document generators).
   * Uses fetchWithRetry for timeout and 401 retry support.
   */
  async getHtml(path: ApiPathWithQuery): Promise<string> {
    return client.getHtml(path);
  },

  /**
   * POST request returning raw HTML (for lease agreement generation).
   * Uses fetchWithRetry for timeout and 401 retry support.
   */
  async postHtml(path: ApiPathWithQuery, data: unknown): Promise<string> {
    return client.postHtml(path, data);
  },

  /**
   * GET request returning a Blob (downloads / exports).
   * Uses fetchWithRetry for timeout and 401 retry support.
   */
  async getBlob(
    path: ApiPathWithQuery,
    opts: FetchRetryOptions = {},
  ): Promise<Blob> {
    return client.getBlob(path, opts);
  },

  /** GET returning raw HTML with dev-mode non-empty check. */
  async validatedGetHtml(path: ApiPathWithQuery): Promise<string> {
    const result = await this.getHtml(path);
    if (import.meta.env.DEV && (!result || result.trim().length === 0)) {
      console.warn(`[API] ${path} returned empty HTML response`);
    }
    return result;
  },

  /** POST returning raw HTML with dev-mode non-empty check. */
  async validatedPostHtml(path: ApiPathWithQuery, data: unknown): Promise<string> {
    const result = await this.postHtml(path, data);
    if (import.meta.env.DEV && (!result || result.trim().length === 0)) {
      console.warn(`[API] ${path} returned empty HTML response`);
    }
    return result;
  },

  /**
   * GET request returning both data and pagination meta.
   */
  async getPaginated<T = unknown>(
    path: ApiPathWithQuery,
    options?: ApiRequestOptions,
  ): Promise<PaginatedResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
    };
    const dedupeKey = !options?.signal ? `GET:${path}:${headers.Authorization ?? ''}` : null;
    if (dedupeKey) {
      const existing = inFlightPaginatedReads.get(dedupeKey);
      if (existing) {
        return existing as Promise<PaginatedResponse<T>>;
      }
    }

    const requestPromise = (async (): Promise<PaginatedResponse<T>> => {
      const response = await fetchWithRetry(
        `${this.baseUrl}${path}`,
        { method: 'GET', headers, signal: options?.signal },
        {},
        refreshAuthToken,
      );

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      const json = await parseJsonEnvelope(response);
      if (!('data' in json)) {
        throw new Error('Invalid API response: missing data envelope');
      }
      return {
        data: json.data as T,
        meta: (json.meta as PaginationMeta) || null,
      };
    })();

    if (!dedupeKey) {
      return requestPromise;
    }

    inFlightPaginatedReads.set(dedupeKey, requestPromise as Promise<PaginatedResponse<unknown>>);
    try {
      return await requestPromise;
    } finally {
      inFlightPaginatedReads.delete(dedupeKey);
    }
  },
};
